/**
 * OfficeDashboardPage (Phase-2, Module 1 — office admin landing)
 *
 * Hub for office administrators: manage offices/staff and access coordinator
 * approval queues. Read-only KPIs derived from the offices list.
 *
 * @author Civilhealth Development Team
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Box, Grid, Card, CardActionArea, CardContent, Typography, Stack } from '@mui/material';
import {
  Business as OfficeIcon, LocalHospital as AdmIcon, Biotech as InvIcon, Shield as InsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PageScaffold, KPICard } from '../../components/common';
import { OfficeApi, type OfficeRow } from '../../services/phase2Api';

const OfficeDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [offices, setOffices] = useState<OfficeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setOffices((await OfficeApi.list({ page: 0, size: 100 })) || []); }
    catch (e: any) { setError(e?.response?.data?.message || 'Failed to load offices'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalStaff = offices.reduce((a, o) => a + (o.staffCount || 0), 0);
  const totalOfficers = offices.reduce((a, o) => a + (o.officerCount || 0), 0);

  const cards = [
    { label: 'Manage Offices & Staff', desc: 'Offices and assigned staff', icon: <OfficeIcon />, path: '/office-management', color: '#3B82F6' },
    { label: 'Admission Approvals', desc: 'IPD admission queue', icon: <AdmIcon />, path: '/admin/admissions', color: '#F59E0B' },
    { label: 'Investigation Approvals', desc: 'Approve & assign labs', icon: <InvIcon />, path: '/admin/investigations', color: '#EC4899' },
    { label: 'Insurance Verification', desc: 'Verify pending policies', icon: <InsIcon />, path: '/admin/insurance', color: '#10B981' },
  ];

  return (
    <PageScaffold title="Office Admin Dashboard" subtitle="Office & healthcare operations oversight" icon={<OfficeIcon />}
      loading={loading} error={error} onDismissError={() => setError(null)}>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}><KPICard title="Total Offices" value={offices.length} subtitle="Registered" icon={<OfficeIcon />} color="primary" /></Grid>
        <Grid item xs={12} sm={6} md={4}><KPICard title="Total Staff" value={totalStaff} subtitle="Across offices" icon={<OfficeIcon />} color="info" /></Grid>
        <Grid item xs={12} sm={6} md={4}><KPICard title="Mapped Officers" value={totalOfficers} subtitle="Across offices" icon={<OfficeIcon />} color="success" /></Grid>
      </Grid>
      <Grid container spacing={2}>
        {cards.map((c) => (
          <Grid item xs={12} sm={6} md={3} key={c.path}>
            <Card variant="outlined" sx={{ borderLeft: `4px solid ${c.color}`, height: '100%' }}>
              <CardActionArea onClick={() => navigate(c.path)} sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ color: c.color }}>{c.icon}</Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>{c.label}</Typography>
                    <Typography variant="body2" color="text.secondary">{c.desc}</Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </PageScaffold>
  );
};

export default OfficeDashboardPage;